/**
 * Energy Breakdown Section — full detail view for analytics page.
 * Stacked bars (sources + destinations) + two donuts + curtailment summary + per-property table.
 * Receives FleetAnalytics data from parent.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, Tooltip as ReTooltip, ResponsiveContainer } from 'recharts';
import { useApp } from '@/contexts/AppContext';
import { cn } from '@/lib/utils';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { FleetAnalytics, FleetPropertyRow } from '@/hooks/useFleetAnalytics';

const COLORS = {
  pv: '#BEA504',
  gridImport: '#60A5FA',
  generator: '#8B92A4',
  load: '#60A5FA',
  battery: '#BEA504',
  gridExport: '#10B981',
  curtailed: '#D97706',
};

const SEGMENT_GAP = 'hsl(222, 20%, 12%)';

// ---------- Stacked Bar ----------

interface BarSegment {
  key: string;
  label: string;
  value: number;
  color: string;
  ghost?: boolean;
}

function AnalyticsStackedBar({ segments, totalLabel }: { segments: BarSegment[]; totalLabel: string }) {
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  if (total === 0) return null;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {segments.filter((s) => !s.ghost && s.value > 0).map((seg) => (
            <span key={seg.key} className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: seg.color }} />
              {seg.label}
            </span>
          ))}
          {segments.some((s) => s.ghost && s.value > 0) && (
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className="h-2 w-2 rounded-full opacity-40" style={{ backgroundColor: COLORS.curtailed }} />
              Curtailed
            </span>
          )}
        </div>
        {totalLabel && <span className="text-xs text-muted-foreground font-mono">{totalLabel}</span>}
      </div>
      <TooltipProvider delayDuration={100}>
        <div className="flex h-8 rounded-md overflow-hidden bg-muted/30">
          {segments.map((seg) => {
            if (seg.value <= 0) return null;
            const pct = (seg.value / total) * 100;
            const showLabel = pct > 12;
            return (
              <Tooltip key={seg.key}>
                <TooltipTrigger asChild>
                  <div
                    className="h-full flex items-center justify-center transition-all"
                    style={{
                      width: `${pct}%`,
                      backgroundColor: seg.color,
                      opacity: seg.ghost ? 0.3 : 1,
                      minWidth: seg.value > 0 ? '2px' : 0,
                    }}
                  >
                    {showLabel && (
                      <span className="text-[11px] font-mono text-white/90 truncate px-1">
                        {seg.value >= 1000
                          ? `${(seg.value / 1000).toFixed(1)}k`
                          : seg.value.toFixed(0)}
                      </span>
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent className="bg-wt-bg-primary border-border text-xs font-mono">
                  {seg.label}: {seg.value.toLocaleString(undefined, { maximumFractionDigits: 1 })} kWh ({pct.toFixed(1)}%)
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </TooltipProvider>
    </div>
  );
}

// ---------- Donut ----------

interface DonutSegment {
  name: string;
  label: string;
  value: number;
  fill: string;
}

function DonutTooltipContent({ active, payload }: { active?: boolean; payload?: Array<{ name: string; value: number; payload: { label: string } }> }) {
  if (!active || !payload?.length) return null;
  const item = payload[0];
  return (
    <div className="rounded-md bg-wt-bg-primary border border-border px-3 py-2 shadow-lg">
      <p className="text-xs text-foreground font-mono">
        {item.payload.label}: {item.value.toLocaleString(undefined, { maximumFractionDigits: 1 })} kWh
      </p>
    </div>
  );
}

function EnergyDonut({ segments, centerKwh, title }: { segments: DonutSegment[]; centerKwh: number; title: string }) {
  const filtered = segments.filter((s) => s.value > 0);
  if (filtered.length === 0) {
    return (
      <div className="flex-1 min-w-[200px] rounded-lg border border-border/50 bg-wt-bg-tertiary/30 p-4 flex flex-col items-center justify-center min-h-[220px]">
        <p className="text-xs text-muted-foreground">{title}</p>
        <p className="text-sm text-muted-foreground mt-2">No data</p>
      </div>
    );
  }

  const total = filtered.reduce((s, seg) => s + seg.value, 0);

  return (
    <div className="flex-1 min-w-[200px] rounded-lg border border-border/50 bg-wt-bg-tertiary/30 p-4 space-y-2">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{title}</p>
      <div className="flex justify-center">
        <div className="relative" style={{ width: 160, height: 160 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <ReTooltip content={<DonutTooltipContent />} />
              <Pie
                data={filtered}
                dataKey="value"
                cx="50%"
                cy="50%"
                innerRadius={48}
                outerRadius={72}
                startAngle={90}
                endAngle={-270}
                stroke={SEGMENT_GAP}
                strokeWidth={2}
              >
                {filtered.map((entry) => (
                  <Cell key={entry.name} fill={entry.fill} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="font-mono text-[16px] font-semibold text-foreground leading-tight">
              {centerKwh >= 1000
                ? `${(centerKwh / 1000).toFixed(1)}k`
                : centerKwh.toFixed(0)}
            </span>
            <span className="text-[10px] text-muted-foreground">kWh</span>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 text-[11px]">
        {filtered.map((seg) => {
          const pct = total > 0 ? ((seg.value / total) * 100).toFixed(0) : '0';
          return (
            <span key={seg.name} className="flex items-center gap-1">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: seg.fill }} />
              <span className="text-muted-foreground">{seg.label} {pct}%</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ---------- Curtailment Summary Card ----------

function CurtailmentSummaryCard({
  curtailedKwh,
  pvKwh,
  rateCents,
  topProperties,
}: {
  curtailedKwh: number;
  pvKwh: number;
  rateCents: number;
  topProperties: { id: string; name: string; kwh: number }[];
}) {
  const navigate = useNavigate();
  const { setPropertyScope } = useApp();
  const dollars = curtailedKwh * (rateCents / 100);
  const potential = pvKwh + curtailedKwh;
  const pct = potential > 0 ? ((curtailedKwh / potential) * 100).toFixed(1) : '0';

  if (curtailedKwh <= 0) {
    return (
      <div className="flex-1 min-w-[200px] rounded-lg border border-border/50 bg-wt-bg-tertiary/30 p-4 flex flex-col items-center justify-center min-h-[220px]">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Curtailment</p>
        <p className="text-sm text-wt-green-primary mt-4 font-medium">No curtailment detected</p>
      </div>
    );
  }

  return (
    <div className="flex-1 min-w-[200px] rounded-lg border border-border/50 bg-wt-bg-tertiary/30 p-4 space-y-3">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Curtailment</p>
      <div className="space-y-1">
        <p className="font-mono text-[22px] font-semibold text-wt-amber-primary">
          {curtailedKwh.toLocaleString(undefined, { maximumFractionDigits: 0 })} kWh
        </p>
        <p className="text-sm text-muted-foreground font-mono">~${dollars.toFixed(0)} wasted</p>
        <p className="text-xs text-muted-foreground">{pct}% of potential</p>
      </div>
      {topProperties.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Top properties</p>
          {topProperties.map((p) => (
            <button
              key={p.id}
              className="flex items-center justify-between w-full text-xs hover:text-primary transition-colors text-left"
              onClick={() => {
                setPropertyScope(p.id, p.name);
                navigate(`/app/property/${p.id}`);
              }}
            >
              <span className="truncate text-foreground">{p.name}</span>
              <span className="font-mono text-muted-foreground ml-2 shrink-0">{p.kwh.toFixed(0)} kWh</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Per-Property Energy Table ----------

type SortKey = 'name' | 'pv_kwh' | 'grid_import_kwh' | 'load_kwh' | 'grid_export_kwh' | 'curtailed_kwh';

function EnergyPropertyTable({ properties, fleetAvgRateCents }: { properties: FleetPropertyRow[]; fleetAvgRateCents: number }) {
  const navigate = useNavigate();
  const { setPropertyScope } = useApp();
  const [sortKey, setSortKey] = useState<SortKey>('grid_import_kwh');
  const [sortDesc, setSortDesc] = useState(true);

  const sorted = useMemo(() => {
    const items = [...properties].filter((p) => p.pv_kwh > 0 || p.grid_import_kwh > 0 || p.load_kwh > 0);
    items.sort((a, b) => {
      const va = a[sortKey] ?? 0;
      const vb = b[sortKey] ?? 0;
      if (typeof va === 'string' && typeof vb === 'string') {
        return sortDesc ? vb.localeCompare(va) : va.localeCompare(vb);
      }
      return sortDesc ? (vb as number) - (va as number) : (va as number) - (vb as number);
    });
    return items;
  }, [properties, sortKey, sortDesc]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDesc(!sortDesc);
    } else {
      setSortKey(key);
      setSortDesc(true);
    }
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return null;
    return sortDesc ? <ChevronDown className="h-3 w-3 inline" /> : <ChevronUp className="h-3 w-3 inline" />;
  };

  const columns: { key: SortKey; label: string; align: string }[] = [
    { key: 'name', label: 'Property', align: 'text-left' },
    { key: 'pv_kwh', label: 'PV kWh', align: 'text-right' },
    { key: 'grid_import_kwh', label: 'Grid In kWh', align: 'text-right' },
    { key: 'load_kwh', label: 'Load kWh', align: 'text-right' },
    { key: 'grid_export_kwh', label: 'Export kWh', align: 'text-right' },
    { key: 'curtailed_kwh', label: 'Curtailed kWh', align: 'text-right' },
  ];

  if (sorted.length === 0) {
    return <p className="text-sm text-muted-foreground py-4 text-center">No energy data for this period.</p>;
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-border/50">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-wt-bg-tertiary/50">
            {columns.map((col) => (
              <th
                key={col.key}
                className={cn('px-3 py-2 text-xs font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors select-none', col.align)}
                onClick={() => handleSort(col.key)}
              >
                {col.label} <SortIcon col={col.key} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((p) => {
            const gridPct = p.load_kwh > 0 ? (p.grid_import_kwh / p.load_kwh) * 100 : 0;
            const curtPct = p.pv_kwh > 0 ? (p.curtailed_kwh / p.pv_kwh) * 100 : 0;
            return (
              <tr
                key={p.id}
                className="border-t border-border/30 hover:bg-wt-bg-tertiary/30 cursor-pointer transition-colors"
                onClick={() => {
                  setPropertyScope(p.id, p.name);
                  navigate(`/app/analytics?property=${p.id}`);
                }}
              >
                <td className="px-3 py-2 text-foreground truncate max-w-[200px]">{p.name}</td>
                <td className="px-3 py-2 text-right font-mono text-foreground">{p.pv_kwh.toFixed(0)}</td>
                <td className={cn('px-3 py-2 text-right font-mono', gridPct > 50 ? 'text-wt-amber-primary' : 'text-foreground')}>
                  {p.grid_import_kwh.toFixed(0)}
                </td>
                <td className="px-3 py-2 text-right font-mono text-foreground">{p.load_kwh.toFixed(0)}</td>
                <td className="px-3 py-2 text-right font-mono text-foreground">{p.grid_export_kwh.toFixed(0)}</td>
                <td className={cn('px-3 py-2 text-right font-mono', curtPct > 15 ? 'text-wt-amber-primary' : 'text-foreground')}>
                  {p.curtailed_kwh.toFixed(0)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Main Section ----------

interface EnergyBreakdownSectionProps {
  data: FleetAnalytics;
}

export function EnergyBreakdownSection({ data }: EnergyBreakdownSectionProps) {
  const pvKwh = data.fleet_pv_kwh;
  const gridImportKwh = data.fleet_grid_import_kwh;
  const loadKwh = data.fleet_load_kwh;
  const batteryChargedKwh = data.fleet_battery_charged_kwh;
  const gridExportKwh = data.fleet_grid_export_kwh;
  const curtailedKwh = data.fleet_curtailed_kwh;
  const totalSources = pvKwh + gridImportKwh;
  const totalPotential = pvKwh + curtailedKwh;
  const totalDestinations = loadKwh + batteryChargedKwh + gridExportKwh;
  const hasCurtailment = curtailedKwh > 0;

  const sourceBarSegments: BarSegment[] = [
    { key: 'pv', label: 'PV', value: pvKwh, color: COLORS.pv },
    { key: 'grid', label: 'Grid Import', value: gridImportKwh, color: COLORS.gridImport },
    { key: 'curtailed', label: 'Curtailed', value: curtailedKwh, color: COLORS.curtailed, ghost: true },
  ];

  const destBarSegments: BarSegment[] = [
    { key: 'load', label: 'Load', value: loadKwh, color: COLORS.load },
    { key: 'battery', label: 'Battery', value: batteryChargedKwh, color: COLORS.battery },
    { key: 'export', label: 'Export', value: gridExportKwh, color: COLORS.gridExport },
  ];

  const sourceDonutSegments: DonutSegment[] = [
    { name: 'pv', label: 'PV', value: pvKwh, fill: COLORS.pv },
    { name: 'grid', label: 'Grid Import', value: gridImportKwh, fill: COLORS.gridImport },
  ];

  const destDonutSegments: DonutSegment[] = [
    { name: 'load', label: 'Load', value: loadKwh, fill: COLORS.load },
    { name: 'battery', label: 'Battery', value: batteryChargedKwh, fill: COLORS.battery },
    { name: 'export', label: 'Export', value: gridExportKwh, fill: COLORS.gridExport },
  ];

  const topCurtailed = useMemo(() => {
    if (!data.properties) return [];
    return [...data.properties]
      .filter((p) => p.curtailed_kwh > 0)
      .sort((a, b) => b.curtailed_kwh - a.curtailed_kwh)
      .slice(0, 3)
      .map((p) => ({ id: p.id, name: p.name, kwh: p.curtailed_kwh }));
  }, [data.properties]);

  return (
    <div className="space-y-5">
      {/* Stacked bars */}
      <div className="rounded-lg border border-border/50 bg-wt-bg-tertiary/30 p-4 space-y-4">
        <div>
          <p className="text-sm font-medium text-foreground mb-2">
            SOURCES <span className="font-mono text-muted-foreground ml-1">{totalSources.toLocaleString(undefined, { maximumFractionDigits: 0 })} kWh</span>
          </p>
          <AnalyticsStackedBar
            segments={sourceBarSegments}
            totalLabel={hasCurtailment ? `${totalPotential.toLocaleString(undefined, { maximumFractionDigits: 0 })} kWh potential` : ''}
          />
        </div>
        <div>
          <p className="text-sm font-medium text-foreground mb-2">
            USED <span className="font-mono text-muted-foreground ml-1">{totalDestinations.toLocaleString(undefined, { maximumFractionDigits: 0 })} kWh</span>
          </p>
          <AnalyticsStackedBar segments={destBarSegments} totalLabel="" />
        </div>
      </div>

      {/* Donuts + Curtailment row */}
      <div className="flex gap-4 flex-wrap">
        <EnergyDonut segments={sourceDonutSegments} centerKwh={totalSources} title="Sources" />
        <EnergyDonut segments={destDonutSegments} centerKwh={totalDestinations} title="Destinations" />
        <CurtailmentSummaryCard
          curtailedKwh={curtailedKwh}
          pvKwh={pvKwh}
          rateCents={data.fleet_avg_rate_cents}
          topProperties={topCurtailed}
        />
      </div>

      {/* Per-property table */}
      {data.properties && data.properties.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Per-Property Energy</p>
          <EnergyPropertyTable properties={data.properties} fleetAvgRateCents={data.fleet_avg_rate_cents} />
        </div>
      )}
    </div>
  );
}
